import { useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";

// VBA code snippets with syntax highlighting colors
const codeSnippets = [{
  text: "Sub ExampleMacro()",
  color: "#60A5FA",
  type: "keyword"
}, {
  text: 'Range("A1").Select',
  color: "#F59E0B",
  type: "string"
}, {
  text: "Worksheets.Add",
  color: "#60A5FA",
  type: "function"
}, {
  text: "For i = 1 To 10",
  color: "#60A5FA",
  type: "keyword"
}, {
  text: "End Sub",
  color: "#60A5FA",
  type: "keyword"
}, {
  text: "Dim ws As Worksheet",
  color: "#60A5FA",
  type: "keyword"
}, {
  text: "Application.ScreenUpdating",
  color: "#60A5FA",
  type: "function"
}, {
  text: "Cells(1, 1).Value = 100",
  color: "#A78BFA",
  type: "number"
}, {
  text: 'MsgBox "Hello World"',
  color: "#F59E0B",
  type: "string"
}, {
  text: "Set wb = ActiveWorkbook",
  color: "#60A5FA",
  type: "keyword"
}, {
  text: 'Selection.Font.Bold = True',
  color: "#60A5FA",
  type: "function"
}, {
  text: 'Sheets("Data").Activate',
  color: "#F59E0B",
  type: "string"
}, {
  text: "With ActiveSheet",
  color: "#60A5FA",
  type: "keyword"
}];
const FloatingSnippet = ({
  snippet,
  delay,
  index,
  initialPosition
}: {
  snippet: {
    text: string;
    color: string;
    type: string;
  };
  delay: number;
  index: number;
  initialPosition: {
    x: number;
    y: number;
  };
}) => {
  const [position, setPosition] = useState(initialPosition);
  const getNewPosition = () => {
    // Keep snippets within 5-95% range
    let x = 5 + Math.random() * 90;
    let y = 5 + Math.random() * 90;

    // Avoid center area (20-80% both axes) - larger exclusion zone
    const attempts = 50;
    for (let i = 0; i < attempts; i++) {
      const inCenterX = x > 20 && x < 80;
      const inCenterY = y > 20 && y < 80;
      if (!(inCenterX && inCenterY)) {
        break;
      }
      // Push to edges more aggressively
      x = Math.random() < 0.5 ? 5 + Math.random() * 15 : 80 + Math.random() * 15;
      y = Math.random() < 0.5 ? 5 + Math.random() * 15 : 80 + Math.random() * 15;
    }
    return {
      x,
      y
    };
  };
  useEffect(() => {
    const interval = setInterval(() => {
      setPosition(getNewPosition());
    }, 15000 + Math.random() * 10000); // 15-25 seconds

    return () => clearInterval(interval);
  }, []);
  return <motion.div initial={{
    left: `${position.x}%`,
    top: `${position.y}%`,
    opacity: 0
  }} animate={{
    left: `${position.x}%`,
    top: `${position.y}%`,
    opacity: 1,
    y: [0, -10, 0],
    x: [0, 5, 0]
  }} transition={{
    left: {
      duration: 12,
      ease: "easeInOut"
    },
    top: {
      duration: 12,
      ease: "easeInOut"
    },
    opacity: {
      duration: 1,
      delay
    },
    y: {
      duration: 4,
      repeat: Infinity,
      ease: "easeInOut",
      delay: delay
    },
    x: {
      duration: 5,
      repeat: Infinity,
      ease: "easeInOut",
      delay: delay + 0.5
    }
  }} className="absolute text-xs md:text-sm font-mono whitespace-nowrap pointer-events-none" style={{
    transform: "translate(-50%, -50%)"
  }}>
      <div className="px-3 py-1.5 rounded-md bg-muted backdrop-blur-sm shadow-lg border border-border">
        <span style={{
        color: snippet.color
      }}>{snippet.text}</span>
      </div>
    </motion.div>;
};
export const GrowingLibrarySection = () => {
  const sectionRef = useRef(null);

  // Create evenly distributed initial positions
  const getInitialPositions = () => {
    const positions: Array<{
      x: number;
      y: number;
    }> = [];
    const gridSize = Math.ceil(Math.sqrt(codeSnippets.length));
    codeSnippets.forEach((_, index) => {
      const row = Math.floor(index / gridSize);
      const col = index % gridSize;

      // Position snippets around the perimeter, avoiding center
      const side = index % 4; // 0=top, 1=right, 2=bottom, 3=left
      let x, y;
      
      if (side === 0) { // Top
        x = 10 + (col * 80 / gridSize);
        y = 5 + Math.random() * 15;
      } else if (side === 1) { // Right
        x = 80 + Math.random() * 15;
        y = 10 + (row * 80 / gridSize);
      } else if (side === 2) { // Bottom
        x = 10 + (col * 80 / gridSize);
        y = 80 + Math.random() * 15;
      } else { // Left
        x = 5 + Math.random() * 15;
        y = 10 + (row * 80 / gridSize);
      }

      // Ensure within bounds
      x = Math.max(5, Math.min(95, x));
      y = Math.max(5, Math.min(95, y));

      positions.push({
        x,
        y
      });
    });
    return positions;
  };
  const [initialPositions] = useState(getInitialPositions());
  const {
    scrollYProgress
  } = useScroll({
    target: sectionRef,
    offset: ["start start", "end end"]
  });
  const macrosY = useTransform(scrollYProgress, [0.1, 0.4], [100, 0]);
  const macrosOpacity = useTransform(scrollYProgress, [0.1, 0.4], [0, 1]);
  const installsY = useTransform(scrollYProgress, [0.5, 0.8], [100, 0]);
  const installsOpacity = useTransform(scrollYProgress, [0.5, 0.8], [0, 1]);
  return <section ref={sectionRef} className="relative bg-background h-[160vh]">
      <div className="sticky top-0 h-screen overflow-hidden flex items-center">
        {/* Floating code snippets */}
        <div className="absolute inset-0 bg-muted/20">
          {codeSnippets.map((snippet, index) => <FloatingSnippet key={index} snippet={snippet} delay={index * 0.3} index={index} initialPosition={initialPositions[index]} />)}
        </div>

        {/* Main content */}
        <div className="container mx-auto px-4 max-w-4xl relative z-10">
          <div className="text-center space-y-8">
            {/* Persistent text */}
            <motion.h2 initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }} className="text-3xl font-bold -mt-24 text-secondary-foreground font-sans md:text-7xl">
              A growing library of
            </motion.h2>

            {/* Animated metrics */}
            <div className="space-y-8 min-h-[200px] flex flex-col justify-center">
              <motion.div style={{
              y: macrosY,
              opacity: macrosOpacity
            }} className="text-5xl md:text-7xl font-bold text-foreground">
                120 Macros
              </motion.div>

              <motion.div style={{
              y: installsY,
              opacity: installsOpacity
            }} className="text-5xl md:text-7xl font-bold text-foreground">
                ​30 Creators    
              </motion.div>
            </div>
          </div>
        </div>
      </div>
    </section>;
};